import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { BudgetItemFinancial, FUND_TYPES } from '../../types/budget';
import { StandardTableHeader } from '../StandardTable';

interface BudgetFinancialsTableProps {
  financials: BudgetItemFinancial[];
}

const formatAmount = (amount: number): string => {
  if (Math.abs(amount) >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(amount) >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const BudgetFinancialsTable: React.FC<BudgetFinancialsTableProps> = ({ financials }) => {
  const fyGroups = financials.reduce<Record<string, BudgetItemFinancial[]>>((acc, f) => {
    if (!acc[f.fiscal_year_label]) acc[f.fiscal_year_label] = [];
    acc[f.fiscal_year_label].push(f);
    return acc;
  }, {});

  const fiscalYears = Object.keys(fyGroups).sort();
  const [expanded, setExpanded] = useState<Set<string>>(new Set(fiscalYears));

  if (financials.length === 0) {
    return (
      <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary text-sm">
        No financial data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {fiscalYears.map((fy) => {
        const isExpanded = expanded.has(fy);
        const rows = fyGroups[fy];

        return (
          <div key={fy} className="card overflow-hidden">
            <button
              onClick={() => {
                const next = new Set(expanded);
                if (next.has(fy)) next.delete(fy);
                else next.add(fy);
                setExpanded(next);
              }}
              className="w-full flex items-center justify-between px-4 py-3 bg-background-tertiary dark:bg-background-dark-tertiary hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary transition-colors"
            >
              <span className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                {fy}
              </span>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
              ) : (
                <ChevronRight className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
              )}
            </button>

            {isExpanded && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <StandardTableHeader>
                    <tr>
                      <th className="text-left px-4 py-2 text-text-secondary dark:text-text-dark-secondary font-medium">
                        Column Type
                      </th>
                      {FUND_TYPES.map((ft) => (
                        <th
                          key={ft.value}
                          className="text-right px-4 py-2 text-text-secondary dark:text-text-dark-secondary font-medium"
                        >
                          {ft.label}
                        </th>
                      ))}
                    </tr>
                  </StandardTableHeader>
                  <tbody>
                    {[...new Set(rows.map((r) => r.column_type))].sort().map((colType) => (
                      <tr
                        key={colType}
                        className="border-b border-border-light dark:border-border-dark last:border-b-0"
                      >
                        <td className="px-4 py-2 text-text-primary dark:text-text-dark-primary capitalize">
                          {colType.replace(/_/g, ' ')}
                        </td>
                        {FUND_TYPES.map((ft) => {
                          const cell = rows.find(
                            (r) => r.column_type === colType && r.fund_type === ft.value
                          );
                          return (
                            <td
                              key={ft.value}
                              className={`text-right px-4 py-2 font-mono text-xs ${
                                cell && cell.amount < 0
                                  ? 'text-error-600 dark:text-error-400'
                                  : 'text-text-primary dark:text-text-dark-primary'
                              }`}
                            >
                              {cell ? formatAmount(cell.amount) : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BudgetFinancialsTable;
